import { IWSUserInviteParent, ICalendarSetting, IAppToken, IDeviceData } from '@app-types/websocket/user.types';
import WebSocketClient from '@clients/web-socket.client';
import { IFamilyMember } from '@app-types/websocket/family.types';
import { CountryType } from '@components/calendar2/holidays/holidayTypes';
import { getFirebaseAnalyticsContext } from '@services/firebase/firebaseAnalyticsContext';
import { IAutoApproveSuggestions, IDisablePreviews } from '@services/user/user.actions';
// import { webSocket } from './user.constants';

const socket = WebSocketClient.getInstance();

export function addNewFamilyMember(data: object) {  
  socket.sendJSON({
    newFamilyMember: data,
  });
}

export function updateFamilyMember(data: IFamilyMember) {  
  socket.sendJSON({
    updateFamilyMember: data,
  });
}

export function reloadResources() {  
  socket.sendJSON({
    refreshResources: 100,
  });
}

function requireFamilyConnection(familyId: number) {
  const current = socket.getSocket();
  // Never queue a family-specific edit across a disconnect or a family switch.
  if (!socket.hasConnection() || !current || current.readyState !== current.OPEN
    || new URL(current.url).pathname !== `/v1/families/${familyId}`) {
    throw new Error('The selected family is not connected');
  }
}

export function activateEmailAddress(requestId: string, familyId: number) {
  requireFamilyConnection(familyId);
  socket.sendJSON({ activateEmailAddress: true, requestId });
}

// Deleting yourself only sets your own role in the group to DELETED; the account and the other groups stay.
export function leaveFamily(requestId: string, familyId: number, accountId: number) {
  requireFamilyConnection(familyId);
  socket.sendJSON({ deleteFamilyMember: accountId, requestId });
}

export function reportProblem(msg: string) {  
  socket.sendJSON({
    reportProblem: JSON.stringify(msg),
  });
}

export function updateAppToken(data: IAppToken) {  
  socket.sendJSON({
    appTokenUpdate: data,
  });
}

export function updateLanguage(data: {language:string, timeZone:string}) {  
  socket.sendJSON({
    userLanguage: data,
  });
}

export function deviceData(data:IDeviceData) {  
  socket.sendJSON({
    deviceData: data,
  });
}

export function onboarding(data: string) {  
  socket.sendJSON({
    onboarding: data,
  });
}

export function changePassword(data: string) {  
  socket.sendJSON({
    newPassword: data,
  });
}

// Servers that predate toxicCheckLevel reject the whole request, so the server must be deployed first.
export function toxicCheckLevel(level: number) {
  socket.sendJSON({
    toxicCheckLevel: level,
  });
}

// Push notifications for the messages I send say "New message". The server acks the requestId and sends my
// FamilyMember, with disablePreviews, to everyone in the family in a familyUpdate.
export function disablePreviews(data: IDisablePreviews) {
  requireFamilyConnection(data.familyId);
  socket.sendJSON({
    requestId: `DISABLE_PREVIEWS:${Date.now()}`,
    disablePreviews: data.disabled,
  });
}

// The server answers every device of the parent in this family with AUTO_APPROVE_SUGGESTIONS.
export function autoApproveSuggestions(data: IAutoApproveSuggestions) {
  requireFamilyConnection(data.familyId);
  socket.sendJSON({
    requestId: `AUTO_APPROVE_SUGGESTIONS:${Date.now()}`,
    autoApproveSuggestions: data.enabled,
  });
}

export function darkModeSetting(darkMode: boolean | undefined) {
  socket.sendJSON({
    darkModeSetting: {darkMode},
  });
}

export function updateEmail(data: string) {
  socket.sendJSON({
    updateEmail: data,
  });
}


export function eraseAccount() {  
  socket.sendJSON({
    eraseAccount: true,
  });
}

export function storeCountry(data: CountryType) {
  socket.sendJSON({
    holidaysCountry: data,
  });
}
export function logout() {  
  socket.sendJSON({
    logout: true,
  }, true);
}

export function updateFamilyColors(data: string[]) {  
  socket.sendJSON({
    familyColors: data,
  });
}

export function updateCalendarSettings(data: ICalendarSetting) {  
  if (data.familyId !== undefined) { requireFamilyConnection(data.familyId); }
  const {accountId, familyId, emailToken, onlyWeeksOf, ...editableSettings} = data;
  socket.sendJSON({
    calendarSettings: editableSettings,
  });
}

// 0 shows every parent's weeks again.
export function setOnlyWeeksOf(parentId: number) {
  socket.sendJSON({
    onlyWeeksOf: parentId,
  });
}

export function updateCalendarSettingsSynch(data: boolean) {  
  socket.sendJSON({
    calendarSynchSettings: data,
  });
}

export function updateCalendarSettingsExclude(data: string[]) {  
  socket.sendJSON({
    calendarSynchExclude: data,
  });
}


export async function inviteParent(data: IWSUserInviteParent): Promise<void> {
  const originatingSocket = socket.getSocket();
  const firebaseAnalytics = await getFirebaseAnalyticsContext();
  if (socket.getSocket() !== originatingSocket) {
    throw new Error('Connection changed while preparing invitation; please retry');
  }
  socket.sendJSON({
    inviteParent: {...data, firebaseAnalytics},
  });
}

export function locationCheck(data: {latitude:string, longitude:string}) {
  socket.sendJSON({
    locationCheck: data
  });
}

export function close() {
  try {
    socket.close(true);
  } catch (e) {
    console.warn('Error closing websocket', e);
  }
}



// WEBPACK FOOTER //
// ./src/services/user/user.websocket.ts